import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import db, { DEXIE_IS_LOADING } from '../../lib/db';
import { buildMessageEntitySnapshots } from '../../lib/entitySnapshots';
import { oxfordComma } from '../../lib/stringUtils';
import { useRetainedFeedMessage } from '../../hooks/useRetainedFeedMessage';

import { EntitySnapshotContext } from '../../contexts/EntitySnapshotContext';
import LocalTime from '../messages/LocalTime';
import RichdownContent from '../messages/RichdownContent';
import AnimatedDots from '../shared/AnimatedDots';
import SettingsTabs from '../shared/SettingsTabs';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalContent } from './ModalContent';
import { ModalHeader } from './ModalHeader';
import { ModalHeaderContent } from './ModalHeaderContent';
import { ModalSubtitle } from './ModalSubtitle';
import { ModalTitle } from './ModalTitle';

const Timestamps = ({ startedAt, endedAt = null, noWrapper = false }) => {
  if (!startedAt) return null;

  const content = (
    <>
      <LocalTime timestamp={startedAt} format="%B %-d, %Y · %-I:%M %p" />
      {endedAt && (
        <span>
          &nbsp;&ndash;&nbsp;
          <LocalTime timestamp={endedAt} format="%-I:%M %p" />
        </span>
      )}
    </>
  );

  if (noWrapper) return content;

  return <div className="text-2xs font-medium text-gray-500 dark:text-neutral-500">{content}</div>;
};

const Transcript = ({ actions, messages, workspaceId }) => {
  const participantProfileIds = useMemo(() => actions?.map((action) => action?.profile_id)?.filter(Boolean) ?? [], [actions]);

  const participantProfiles = useLiveQuery(
    () =>
      participantProfileIds.length && workspaceId
        ? db.profiles
            .where('[workspace_id+id]')
            .anyOf(participantProfileIds.map((id) => [workspaceId, id]))
            .toArray()
        : [],
    [participantProfileIds.join('|'), workspaceId],
    DEXIE_IS_LOADING,
  );

  const profileMap = useMemo(() => {
    return participantProfiles !== DEXIE_IS_LOADING && participantProfiles?.length
      ? participantProfiles.reduce((acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        }, {})
      : {};
  }, [participantProfiles]);

  if (participantProfiles === DEXIE_IS_LOADING || !participantProfiles?.length) return null;

  if (!messages?.length) {
    return (
      <div className="flex flex-col gap-4 pt-4 text-cursed">
        <div className="text-cursed text-gray-500 italic dark:text-neutral-200">No recorded conversation.</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-5">
      <div className="flex flex-wrap gap-1">
        {participantProfiles.map((profile, index) => (
          <div key={profile.id} className="text-sm text-gray-950 dark:text-neutral-200">
            <span className="font-semibold">{profile.full_name}</span>
            {oxfordComma(index, participantProfiles.length)}
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        {messages?.map((message, index) => (
          <div key={`message-${index}`} className="flex gap-7">
            <div className="shrink-0 pl-0.5 text-[11px]/5 text-gray-400 dark:text-neutral-500">
              <LocalTime timestamp={message.timestamp} format="%-I:%M %p" />
            </div>
            <div className="flex flex-1 flex-col">
              <div className="text-[11px]/5 font-semibold text-black dark:text-neutral-200">
                {profileMap[message.profile_id]?.full_name || 'Speaker'}
              </div>
              <div className="text-cursed text-gray-500 dark:text-neutral-200">{message.text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Summary = ({ ended_at, title, overview, topics }) => {
  const entitySnapshots = useContext(EntitySnapshotContext);
  const titleEntitySnapshots = useMemo(() => buildMessageEntitySnapshots(title, entitySnapshots), [title, entitySnapshots]);
  const overviewEntitySnapshots = useMemo(() => buildMessageEntitySnapshots(overview, entitySnapshots), [overview, entitySnapshots]);

  const topicsWithEntitySnapshots = useMemo(() => {
    if (!topics?.length) return [];
    return topics.map((topic) => ({
      ...topic,
      titleEntitySnapshots: buildMessageEntitySnapshots(topic.title, entitySnapshots),
      noteEntitySnapshots: topic.notes?.map((note) => buildMessageEntitySnapshots(note, entitySnapshots)) ?? [],
    }));
  }, [topics, entitySnapshots]);

  const isProcessing = ended_at && !title;

  if (isProcessing) {
    return (
      <div className="flex flex-col gap-4 pt-4 text-cursed">
        <div>
          Summarizing
          <AnimatedDots />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pt-4 text-cursed">
      {title && (
        <div className="text-xl font-semibold text-black dark:text-neutral-200">
          <RichdownContent html={title} entitySnapshots={titleEntitySnapshots} richdownVariant="inline" />
        </div>
      )}

      {overview && (
        <div className="text-cursed text-black dark:text-neutral-200">
          <RichdownContent html={overview} entitySnapshots={overviewEntitySnapshots} richdownVariant="inline" />
        </div>
      )}

      {topicsWithEntitySnapshots?.length > 0 && (
        <div className="flex flex-col gap-3">
          {topicsWithEntitySnapshots.map((topic) => (
            <div key={topic.id} className="flex flex-col gap-2">
              <div className="font-semibold text-black dark:text-neutral-200">
                <RichdownContent html={topic.title} entitySnapshots={topic.titleEntitySnapshots} richdownVariant="inline" />
              </div>
              {topic.notes?.length > 0 && (
                <ul className="ml-4 flex flex-col gap-1 text-cursed text-black dark:text-neutral-200">
                  {topic.notes.map((note, index) => (
                    <li key={`${topic.id}-note-${index}`} className="list-disc">
                      <RichdownContent html={note} entitySnapshots={topic.noteEntitySnapshots[index]} richdownVariant="inline" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// `conferenceTranscript` is the rendered transcript row retained when the modal opened;
// the durable feed row is preferred while it exists so an ongoing transcription stays live.
const ConferenceTranscriptModal = ({ conferenceTranscript: retainedTranscript, onClose }) => {
  const [activeTab, setActiveTab] = useState('summary');

  const tabs = useMemo(
    () => [
      { id: 'summary', label: 'Summary' },
      { id: 'transcript', label: 'Transcript' },
    ],
    [],
  );

  const { message: conference_transcript, deleted } = useRetainedFeedMessage(retainedTranscript?.id, retainedTranscript);

  // Sync-originated deletions never dispatch the live-journal destroy event that
  // MessageFeed listens for; close rather than keep showing retained content.
  useEffect(() => {
    if (deleted) onClose?.();
  }, [deleted, onClose]);

  const isLoaded = Boolean(conference_transcript?.data);
  const workspaceId = conference_transcript?.workspace_id;

  const { started_at, ended_at, transcription_paused, actions, messages, title, overview, topics } = conference_transcript?.data ?? {};

  const isOngoing = started_at && !ended_at;

  return (
    <Modal onClose={onClose}>
      <ModalBody className="flex h-full flex-col">
        <ModalHeaderContent className="border-b border-black/5 pb-4 dark:border-white/5">
          <ModalHeader>
            {isLoaded && (
              <ModalSubtitle>
                <Timestamps startedAt={started_at} endedAt={ended_at} noWrapper />
              </ModalSubtitle>
            )}
            <ModalTitle>AV Transcript</ModalTitle>
          </ModalHeader>

          {/* Show tabs only when conference has ended */}
          {isLoaded && !isOngoing && <SettingsTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />}
        </ModalHeaderContent>

        <ModalContent>
          {/* Ongoing: show transcribing state */}
          {isLoaded && isOngoing && (
            <div className="flex flex-col gap-4 pt-4">
              <div className="rounded-sm bg-red-50 px-3.5 py-2 text-cursed text-red-700 dark:bg-red-950 dark:text-red-300">
                {transcription_paused ? 'Transcription paused' : 'Transcribing'}
              </div>
              <Transcript actions={actions} messages={messages} workspaceId={workspaceId} />
            </div>
          )}

          {/* Ended: show tab content */}
          {isLoaded && !isOngoing && (
            <>
              {activeTab === 'transcript' && <Transcript actions={actions} messages={messages} workspaceId={workspaceId} />}
              {activeTab === 'summary' && <Summary ended_at={ended_at} title={title} overview={overview} topics={topics} />}
            </>
          )}
        </ModalContent>
      </ModalBody>
    </Modal>
  );
};

export default ConferenceTranscriptModal;
